import { KeyboardEvent, ReactNode, useCallback, useEffect, useRef } from 'react'
import { theme } from '@factorialco/deprecated-design-system'
import { useF0Dialog } from '@factorialco/f0-react'
import { createPortal } from 'react-dom'

import type { AnchorRect } from 'modules/job_catalog/benchmark/types'

interface Props {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  ariaLabel: string
  anchorRect?: AnchorRect | null
}

/**
 * Low-level Popover component providing full layout control without design system constraints.
 * Uses a portal to render content without imposing a visual shell or requiring a trigger element.
 * Handles Escape key and focuses the dialog container on open for keyboard accessibility.
 *
 * @example
 * const [isOpen, setIsOpen] = useState(false)
 * return (
 *   <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} ariaLabel="Details">
 *     <YourCustomContent />
 *   </Popover>
 * )
 */
const Popover = ({ isOpen, onClose, children, ariaLabel, anchorRect }: Props) => {
  const { portalContainer } = useF0Dialog()
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      overlayRef.current?.focus()
    }
  }, [isOpen])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    },
    [onClose]
  )

  if (!isOpen) return null

  return createPortal(
    // eslint-disable-next-line factorial/no-raw-html -- Portal overlay requires fixed positioning + inset + zIndex unsupported by F0Box
    <div
      ref={overlayRef}
      data-benchmark-popover-portal
      role="dialog"
      aria-modal
      aria-label={ariaLabel}
      tabIndex={-1}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.modals + 1,
        pointerEvents: 'auto',
        outline: 'none',
        background: 'hsl(var(--neutral-20) / 0.3)',
      }}
      onClick={() => {
        onClose()
      }}
      onKeyDown={handleKeyDown}
    >
      {/* eslint-disable-next-line factorial/no-raw-html -- Absolute positioning for anchored popover placement unsupported by F0Box */}
      <div
        style={{
          position: 'absolute',
          top: anchorRect ? anchorRect.top + anchorRect.height + 8 : '50%',
          left: anchorRect ? anchorRect.left + anchorRect.width : '50%',
          transform: anchorRect ? 'translateX(-100%)' : 'translate(-50%, -50%)',
          pointerEvents: 'auto',
        }}
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        {children}
      </div>
    </div>,
    portalContainer ?? document.body
  )
}

export default Popover
